import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Users, Check, X, UserPlus, Search, KeyRound, Trash2, Shield, ShieldOff, Loader2,
    Filter, Copy, ChevronDown, Info, Link as LinkIcon,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";

const ROLES = [
    { value: "doctor", label: "Médico(a) · Admin", group: "Profissional", license: "CRM" },
    { value: "nurse", label: "Enfermeiro(a)", group: "Profissional", license: "COREN" },
    { value: "physiotherapist", label: "Fisioterapeuta", group: "Profissional", license: "CREFITO" },
    { value: "occupational_therapist", label: "Terapeuta Ocupacional", group: "Profissional", license: "CREFITO" },
    { value: "nutritionist", label: "Nutricionista", group: "Profissional", license: "CRN" },
    { value: "psychologist", label: "Psicólogo(a)", group: "Profissional", license: "CRP" },
    { value: "social_worker", label: "Assistente Social", group: "Profissional", license: "CRESS" },
    { value: "pharmacist", label: "Farmacêutico(a)", group: "Profissional", license: "CRF" },
    { value: "transpersonal_therapist", label: "Terapeuta Transpessoal", group: "Profissional", license: "Registro" },
    { value: "patient", label: "Paciente", group: "Uso pessoal" },
    { value: "caregiver", label: "Cuidador(a)", group: "Uso pessoal" },
];

const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r;
const roleGroup = (r) => ROLES.find((x) => x.value === r)?.group || "";
const groupedRoles = ROLES.reduce((acc, r) => { (acc[r.group] ||= []).push(r); return acc; }, {});

export default function AdminUsersPage() {
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [pendingResets, setPendingResets] = useState([]);
    const [filters, setFilters] = useState({ status: "", role: "", search: "" });
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);   // user object being confirmed

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.role) params.role = filters.role;
            if (filters.search) params.search = filters.search;
            const [u, s, r] = await Promise.all([
                api.get("/admin/users", { params }),
                api.get("/admin/users/stats"),
                api.get("/admin/password-resets/pending"),
            ]);
            setUsers(u.data);
            setStats(s.data);
            setPendingResets(r.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    // Reload on filter changes.
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters.status, filters.role, filters.search]);

    const call = async (fn, successMsg) => {
        try {
            const r = await fn();
            if (successMsg) toast.success(successMsg);
            await load();
            return r;
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const approve = (id) => call(() => api.put(`/admin/users/${id}/approve`), "Cadastro aprovado.");
    const reject = (id) => window.confirm("Rejeitar este cadastro? A conta será desativada.") && call(() => api.put(`/admin/users/${id}/reject`), "Cadastro rejeitado.");
    const activate = (id) => call(() => api.put(`/admin/users/${id}/activate`), "Conta ativada.");
    const deactivate = (id) => window.confirm("Desativar esta conta?") && call(() => api.put(`/admin/users/${id}/deactivate`), "Conta desativada.");
    const softDelete = (id) => {
        // Old soft-delete replaced by hard-delete modal — kept name for compat with UserRow prop
        const u = users.find((x) => x.id === id);
        if (u) setDeleteTarget(u);
    };
    const confirmHardDelete = async () => {
        if (!deleteTarget) return;
        try {
            const { data } = await api.delete(`/admin/users/${deleteTarget.id}`);
            const d = data.deleted || {};
            toast.success(
                `Usuário excluído. ${d.password_reset_tokens || 0} tokens, ${d.consents || 0} consentimentos e ${d.caregiver_links_as_caregiver || 0} vínculos removidos.`,
                { duration: 6000 }
            );
            setDeleteTarget(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const changeRole = (id, role) => call(() => api.put(`/admin/users/${id}/role`, { role }), "Papel atualizado.");
    const resetPw = async (id) => {
        if (!window.confirm("Gerar uma senha temporária para este usuário? A senha atual será substituída.")) return;
        const r = await call(() => api.post(`/admin/users/${id}/reset-password`, {}));
        const pw = r?.data?.temporary_password;
        if (pw) {
            navigator.clipboard?.writeText(pw).catch(() => { });
            toast.success(`Senha temporária: ${pw} (copiada)`, { duration: 12000 });
        }
    };

    return (
        <AppShell>
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Administração</div>
                    <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Usuários & Permissões</h1>
                    <p className="text-sm text-muted-clinical mt-1">Aprove profissionais, ajuste papéis, redefina senhas e audite acessos LGPD.</p>
                </div>
                <button data-testid="admin-new-user-btn" onClick={() => setShowCreate(true)} className="btn-primary">
                    <UserPlus size={14} /> Criar usuário
                </button>
            </div>

            {/* Kit Memed — download dos documentos gerados para o formulário oficial */}
            <div className="card-earthy mb-6 border-l-4 border-[#2E7D6B]" data-testid="memed-kit-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <h3 className="font-display text-lg text-brand mb-1">Kit de avaliação Memed</h3>
                        <p className="text-sm text-muted-clinical">
                            Documentos prontos para enviar à equipe da Memed no formulário oficial de parceiros.
                            A conta <b>avaliador.memed@clinicamedicafernandes.com.br</b> é isolada por LGPD e só vê os
                            2 pacientes fictícios criados para a avaliação.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/memed-evaluator/form-answers`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary text-sm"
                            data-testid="download-memed-form-answers"
                        >
                            📋 Respostas do formulário
                        </a>
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/memed-evaluator/guide`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary text-sm"
                            data-testid="download-memed-guide"
                        >
                            📖 Guia técnico completo
                        </a>
                    </div>
                </div>
            </div>

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <StatCard label="Total" value={stats.total} />
                    <StatCard label="Pendentes" value={stats.pending} tone={stats.pending > 0 ? "warning" : "default"} />
                    <StatCard label="Inativos" value={stats.inactive} tone="muted" />
                    <StatCard label="Profissionais" value={
                        Object.entries(stats.by_role || {})
                            .filter(([r]) => roleGroup(r) === "Profissional")
                            .reduce((s, [, n]) => s + n, 0)
                    } />
                </div>
            )}

            {pendingResets.length > 0 && (
                <div className="card-earthy mb-4 border-l-4 border-[#EDD9A6]" data-testid="pending-resets">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="font-display text-lg text-brand flex items-center gap-2"><KeyRound size={16} /> Solicitações de recuperação de senha</h3>
                            <p className="text-xs text-muted-clinical">Copie o link e compartilhe com o usuário por canal seguro (WhatsApp, ligação, e-mail pessoal).</p>
                        </div>
                    </div>
                    <div className="space-y-2">
                        {pendingResets.map((r) => {
                            const link = `${window.location.origin}/reset-password/${r.token}`;
                            const copyLink = () => {
                                navigator.clipboard?.writeText(link).catch(() => { });
                                toast.success("Link copiado.");
                            };
                            const revoke = async () => {
                                if (!window.confirm("Revogar este link de recuperação?")) return;
                                try {
                                    await api.delete(`/admin/password-resets/${r.token}`);
                                    toast.success("Link revogado.");
                                    load();
                                } catch (e) { toast.error(formatApiError(e)); }
                            };
                            return (
                                <div key={r.token} data-testid={`reset-req-${r.user_email}`} className="flex items-center gap-3 bg-[#FBF4E5] rounded-lg p-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-brand truncate">{r.user_name} — {r.user_email}</div>
                                        <div className="text-xs text-muted-clinical truncate font-mono">{link}</div>
                                    </div>
                                    <button data-testid={`copy-reset-${r.user_email}`} onClick={copyLink} className="btn-primary text-xs"><Copy size={12} /> Copiar link</button>
                                    <button onClick={revoke} className="btn-secondary text-xs text-[var(--status-danger)]"><X size={12} /> Revogar</button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="card-earthy mb-4">
                <div className="flex flex-wrap gap-3 items-end">
                    <div className="relative flex-1 min-w-[220px]">
                        <label className="label-clinical">Buscar</label>
                        <input
                            data-testid="admin-search"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            placeholder="Nome ou e-mail..."
                            className="input-clinical pl-9"
                        />
                        <Search size={14} className="absolute left-3 bottom-3 text-muted-clinical" />
                    </div>
                    <div>
                        <label className="label-clinical">Status</label>
                        <select data-testid="admin-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="input-clinical">
                            <option value="">Todos</option>
                            <option value="pending">Pendentes</option>
                            <option value="active">Ativos</option>
                            <option value="inactive">Inativos</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-clinical">Papel</label>
                        <select data-testid="admin-filter-role" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} className="input-clinical">
                            <option value="">Todos</option>
                            {Object.entries(groupedRoles).map(([g, list]) => (
                                <optgroup key={g} label={g}>
                                    {list.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand" size={32} /></div>
            ) : users.length === 0 ? (
                <div className="card-earthy text-center text-sm text-muted-clinical py-14" data-testid="admin-empty">
                    <Users size={32} className="text-brand mx-auto mb-3 opacity-60" />
                    Nenhum usuário encontrado com os filtros aplicados.
                </div>
            ) : (
                <div className="space-y-2">
                    {users.map((u) => (
                        <UserRow
                            key={u.id}
                            u={u}
                            onApprove={() => approve(u.id)}
                            onReject={() => reject(u.id)}
                            onActivate={() => activate(u.id)}
                            onDeactivate={() => deactivate(u.id)}
                            onDelete={() => softDelete(u.id)}
                            onResetPw={() => resetPw(u.id)}
                            onChangeRole={(r) => changeRole(u.id, r)}
                        />
                    ))}
                </div>
            )}

            {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />}
            {deleteTarget && (
                <DeleteUserModal
                    user={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={confirmHardDelete}
                />
            )}
        </AppShell>
    );
}

function StatCard({ label, value, tone = "default" }) {
    const tones = {
        default: "border-[rgba(30,63,51,0.1)] bg-white",
        warning: "border-[#EDD9A6] bg-[#FBF4E5]",
        muted: "border-[rgba(30,63,51,0.08)] bg-alt",
    };
    return (
        <div data-testid={`stat-${label.toLowerCase()}`} className={`border rounded-xl p-4 ${tones[tone]}`}>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-clinical">{label}</div>
            <div className="font-display text-3xl text-brand font-medium mt-1">{value}</div>
        </div>
    );
}

function UserRow({ u, onApprove, onReject, onActivate, onDeactivate, onDelete, onResetPw, onChangeRole }) {
    const [open, setOpen] = useState(false);
    const status = u.pending_approval ? "pending" : u.active ? "active" : "inactive";
    const statusMeta = {
        pending: { label: "Pendente", cls: "bg-[#FBF4E5] text-[#8A6A15]" },
        active: { label: "Ativo", cls: "bg-[#E5EFE0] text-[#315B3A]" },
        inactive: { label: "Inativo", cls: "bg-[#F0E0DC] text-[#7A2E1F]" },
    }[status];

    return (
        <div data-testid={`user-row-${u.id}`} className="card-earthy">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-full bg-alt text-brand flex items-center justify-center font-semibold shrink-0">
                    {u.name?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand truncate">{u.name}</div>
                    <div className="text-xs text-muted-clinical truncate">{u.email}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${statusMeta.cls}`}>
                    {statusMeta.label}
                </span>
                <span className="text-xs bg-alt text-brand px-2 py-1 rounded-full">{roleLabel(u.role)}</span>
                <div className="flex gap-1 shrink-0">
                    {status === "pending" && (
                        <>
                            <button data-testid={`approve-${u.id}`} onClick={onApprove} className="btn-primary text-xs" title="Aprovar cadastro"><Check size={12} /></button>
                            <button data-testid={`reject-${u.id}`} onClick={onReject} className="btn-secondary text-xs text-[var(--status-danger)]" title="Rejeitar"><X size={12} /></button>
                        </>
                    )}
                    <button onClick={() => setOpen((x) => !x)} data-testid={`toggle-${u.id}`} className="btn-ghost text-xs" title="Ações"><ChevronDown size={14} className={open ? "rotate-180 transition-transform" : "transition-transform"} /></button>
                </div>
            </div>
            {open && (
                <div className="mt-4 pt-4 border-t border-[rgba(30,63,51,0.08)] grid md:grid-cols-2 gap-3 text-sm">
                    <div className="space-y-1">
                        {u.crm && <div><span className="text-muted-clinical">Registro:</span> <strong className="text-brand">{u.crm}</strong></div>}
                        {u.specialty && <div><span className="text-muted-clinical">Especialidade:</span> {u.specialty}</div>}
                        {u.phone && <div><span className="text-muted-clinical">Telefone:</span> {u.phone}</div>}
                        {u.created_at && <div><span className="text-muted-clinical">Criado em:</span> {new Date(u.created_at).toLocaleDateString("pt-BR")}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 md:justify-end items-start">
                        <label className="text-xs">
                            <span className="label-clinical">Alterar papel</span>
                            <select
                                data-testid={`role-select-${u.id}`}
                                value={u.role}
                                onChange={(e) => onChangeRole(e.target.value)}
                                className="input-clinical text-xs w-48"
                            >
                                {Object.entries(groupedRoles).map(([g, list]) => (
                                    <optgroup key={g} label={g}>
                                        {list.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        </label>
                        <div className="flex flex-wrap gap-2 mt-6">
                            <button data-testid={`resetpw-${u.id}`} onClick={onResetPw} className="btn-secondary text-xs"><KeyRound size={12} /> Reset senha</button>
                            {u.active ? (
                                <button data-testid={`deactivate-${u.id}`} onClick={onDeactivate} className="btn-secondary text-xs"><ShieldOff size={12} /> Desativar</button>
                            ) : (
                                <button data-testid={`activate-${u.id}`} onClick={onActivate} className="btn-secondary text-xs"><Shield size={12} /> Ativar</button>
                            )}
                            <button data-testid={`delete-${u.id}`} onClick={onDelete} className="btn-secondary text-xs text-[var(--status-danger)]"><Trash2 size={12} /> Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CreateUserModal({ onClose, onCreated }) {
    const [f, setF] = useState({ name: "", email: "", role: "patient", crm: "", specialty: "", phone: "", password: "" });
    const [saving, setSaving] = useState(false);
    const [genPw, setGenPw] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...f, password: f.password || undefined };
            const { data } = await api.post("/admin/users", payload);
            if (data.generated_password) {
                setGenPw(data.generated_password);
                navigator.clipboard?.writeText(data.generated_password).catch(() => { });
                toast.success("Usuário criado. Senha temporária copiada.");
            } else {
                toast.success("Usuário criado.");
                onCreated();
                onClose();
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 my-8 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-muted-clinical hover:text-brand"><X size={16} /></button>
                <h3 className="font-display text-2xl font-medium text-brand mb-1">Criar usuário</h3>
                <p className="text-sm text-muted-clinical mb-5">Preencha os dados. Se você não definir uma senha, uma senha temporária forte será gerada e copiada.</p>

                {genPw ? (
                    <div className="space-y-4">
                        <div className="bg-[#FBF4E5] border-l-4 border-[#EDD9A6] p-4 rounded">
                            <div className="text-xs uppercase tracking-wider text-[#8A6A15] mb-2 flex items-center gap-2"><Info size={12} /> Senha temporária gerada</div>
                            <div className="font-mono text-lg text-brand select-all">{genPw}</div>
                            <p className="text-xs text-muted-clinical mt-2">Compartilhe com o usuário por canal seguro. Ele poderá alterá-la depois.</p>
                        </div>
                        <button onClick={() => { navigator.clipboard?.writeText(genPw); toast.success("Copiado"); }} className="btn-secondary w-full text-sm"><Copy size={14} /> Copiar novamente</button>
                        <button onClick={() => { onCreated(); onClose(); }} className="btn-primary w-full">Fechar</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div><label className="label-clinical">Nome</label><input data-testid="new-user-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-clinical" /></div>
                        <div><label className="label-clinical">E-mail</label><input data-testid="new-user-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-clinical" /></div>
                        <div>
                            <label className="label-clinical">Papel</label>
                            <select data-testid="new-user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="input-clinical">
                                {Object.entries(groupedRoles).map(([g, list]) => (
                                    <optgroup key={g} label={g}>
                                        {list.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="label-clinical">Registro</label><input value={f.crm} onChange={(e) => setF({ ...f, crm: e.target.value })} className="input-clinical" /></div>
                            <div><label className="label-clinical">Especialidade</label><input value={f.specialty} onChange={(e) => setF({ ...f, specialty: e.target.value })} className="input-clinical" /></div>
                        </div>
                        <div><label className="label-clinical">Senha (opcional — deixe vazio para gerar)</label><input data-testid="new-user-password" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="input-clinical" placeholder="Mín. 8 c/ maiúscula, número e símbolo" /></div>
                        <button data-testid="new-user-submit" disabled={saving} className="btn-primary w-full">
                            {saving && <Loader2 size={14} className="animate-spin" />} Criar
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}


function DeleteUserModal({ user, onClose, onConfirm }) {
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const matches = confirmText.trim().toLowerCase() === (user.email || "").trim().toLowerCase();

    const handle = async () => {
        setDeleting(true);
        try { await onConfirm(); } finally { setDeleting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-user-modal">
            <div className="card-earthy max-w-md w-full space-y-4 bg-white">
                <div className="flex items-center gap-3 text-[var(--status-danger)]">
                    <Trash2 size={24} />
                    <h2 className="font-display text-xl font-medium">Excluir usuário</h2>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                    Esta ação é <strong>irreversível</strong>. O usuário{" "}
                    <strong className="text-brand">{user.name}</strong> ({user.email}) será removido
                    do sistema junto com tokens de senha, consentimentos e vínculos de cuidador.
                    Prontuários de pacientes vinculados são preservados (apenas a referência ao usuário é limpa).
                </p>
                <div>
                    <label className="label-clinical">Para confirmar, digite o e-mail exato do usuário:</label>
                    <input
                        data-testid="delete-user-confirm-input"
                        type="email"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={user.email}
                        className="input-clinical"
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="btn-ghost" disabled={deleting}>Cancelar</button>
                    <button
                        data-testid="delete-user-confirm-btn"
                        onClick={handle}
                        disabled={!matches || deleting}
                        className="px-4 py-2 rounded-lg bg-[var(--status-danger)] text-white font-medium hover:bg-[#7a1f21] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        {deleting ? "Excluindo..." : "Excluir definitivamente"}
                    </button>
                </div>
            </div>
        </div>
    );
}
